import React from "react";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    LineChart,
    Line,
    ComposedChart,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    PieChart,
    Pie,
    Cell,
    Legend,
} from "recharts";

function EmptyChart({ height = 220, label = "No data available" }) {
    return (
        <div className="d-flex align-items-center justify-content-center text-muted" style={{ height, fontSize: 13 }}>
            {label}
        </div>
    );
}

export function MarksDistributionChart({ data }) {
    if (!data || data.length === 0) return <EmptyChart />;
    return (
        <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
                <BarChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" hide={data.length > 12} tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={50} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip shared={false} />
                    <Bar dataKey="marks" fill="#2563eb" radius={[4, 4, 0, 0]} isAnimationActive={false} label={{ position: "top", fontSize: 10, fill: "#2563eb", fontWeight: 700 }} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

export function PassFailDonut({ pass, fail }) {
    const data = [
        { name: "Pass", value: pass },
        { name: "Fail", value: fail },
    ];
    const total = pass + fail;
    if (!total) return <EmptyChart height={200} />;
    return (
        <div style={{ width: "100%", height: 200 }}>
            <ResponsiveContainer>
                <PieChart>
                    <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={2} isAnimationActive={false} label={({ name, value }) => `${name}: ${value}`}>
                        <Cell fill="#16a34a" />
                        <Cell fill="#dc2626" />
                    </Pie>
                    <Tooltip shared={false} />
                    <Legend />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}

export function TopBottomChart({ data, dataKey = "marksNum", nameKey = "student_name", color = "#2563eb", height = 260 }) {
    if (!data || data.length === 0) return <EmptyChart height={height} />;
    const chartData = data.map((d) => ({ name: d[nameKey], value: d[dataKey] }));
    return (
        <div style={{ width: "100%", height }}>
            <ResponsiveContainer>
                <BarChart data={chartData} layout="vertical" margin={{ left: 20, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={100} />
                    <Tooltip shared={false} />
                    <Bar dataKey="value" fill={color} radius={[0, 4, 4, 0]} isAnimationActive={false} label={{ position: "right", fontSize: 10.5, fill: color, fontWeight: 700 }} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

export function LeaderboardLineBarChart({ data, height = 320, chartType = "composed" }) {
    if (!data || data.length === 0) return <EmptyChart height={height} />;
    const chartData = data.map((d) => ({
        name: d.student_name || d.name || "Student",
        marks: Number(d.marksNum ?? d.marks ?? 0),
        accuracy: Number(d.accuracyNum ?? d.accuracy ?? 0),
    }));

    const showBar = chartType === "composed" || chartType === "bar";
    const showLine = chartType === "composed" || chartType === "line";

    // 50px per student so all student labels & bars render cleanly with horizontal scroll
    const calculatedWidth = Math.max(100, chartData.length * 50);

    return (
        <div style={{ width: "100%", overflowX: "auto", overflowY: "hidden", paddingBottom: 6 }}>
            <div style={{ width: `${calculatedWidth}px`, minWidth: "100%", height }}>
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 18, right: 20, bottom: 25, left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                        <XAxis
                            dataKey="name"
                            tick={{ fontSize: 10.5, fill: "#475569" }}
                            interval={0}
                            angle={-30}
                            textAnchor="end"
                            height={60}
                        />
                        {showBar && (
                            <YAxis yAxisId="left" tick={{ fontSize: 11, fill: "#2563eb" }} label={{ value: "Marks", angle: -90, position: "insideLeft", fontSize: 10.5, fill: "#2563eb" }} />
                        )}
                        {showLine && (
                            <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: "#16a34a" }} label={{ value: "Accuracy %", angle: 90, position: "insideRight", fontSize: 10.5, fill: "#16a34a" }} />
                        )}
                        <Tooltip
                            contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12 }}
                            formatter={(value, name) => [name === "Accuracy %" ? `${value}%` : `${value} pts`, name]}
                        />
                        <Legend wrapperStyle={{ fontSize: 12, paddingTop: 4 }} />
                        {showBar && (
                            <Bar yAxisId="left" dataKey="marks" name="Marks" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={38} isAnimationActive={false} label={{ position: "top", fontSize: 10, fill: "#2563eb", fontWeight: 700 }} />
                        )}
                        {showLine && (
                            <Line yAxisId="right" type="monotone" dataKey="accuracy" name="Accuracy %" stroke="#16a34a" strokeWidth={2.5} isAnimationActive={false} dot={{ r: 4, fill: "#16a34a", strokeWidth: 2, stroke: "#fff" }} label={{ position: "top", fontSize: 10, fill: "#16a34a", fontWeight: 700, formatter: (val) => `${val}%` }} />
                        )}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default MarksDistributionChart;
